import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Button, Divider,
  Grid, Chip, Avatar, IconButton, Breadcrumbs, Link as MuiLink,
  CircularProgress, useTheme, alpha, Card
} from '@mui/material';
import {
  MenuOpen as MenuOpenIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AccountBalance as InvestorIcon,
  Flag as GoalIcon,
  MonetizationOn as ROIIcon,
  MonetizationOn,
  Assessment as ProposalIcon,
  ArticleOutlined as DocumentIcon,
  Launch as LaunchIcon,
  NavigateNext as NavigateNextIcon,
  Visibility
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { styled } from '@mui/material/styles';
import Sidebar from '../components/common/Sidebar';
import * as marketplaceApi from '../services/marketplace';
import ContactModal from '../components/marketplace/ContactModal';
import { useAuth } from '../context/AuthContext';

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);
const MotionCard = motion(Card);

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  position: 'relative',
  paddingBottom: theme.spacing(1),
  marginBottom: theme.spacing(2),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
  }
}));

const drawerWidth = 240;

interface MainContentProps {
  isSidebarOpen: boolean;
  children: React.ReactNode;
}

const MainContent: React.FC<MainContentProps> = ({ isSidebarOpen, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flexGrow: 1,
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: isSidebarOpen ? drawerWidth : 0,
        width: '100%'
      }}
    >
      {children}
    </Box>
  );
};

const InvestorRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();

  const [request, setRequest] = useState<marketplaceApi.InvestorRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [similarRequests, setSimilarRequests] = useState<marketplaceApi.InvestorRequest[]>([]);

  useEffect(() => {
    const loadRequest = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await marketplaceApi.getInvestorRequestById(id);
        setRequest(data);

        // Load similar requests
        const allRequests = await marketplaceApi.getAllInvestorRequests({ sector: data.sector });
        setSimilarRequests(allRequests.filter(r => r.id !== id).slice(0, 3));
      } catch (error) {
        console.error('Error loading investor request:', error);
        setRequest(null);
      } finally {
        setLoading(false);
      }
    };

    loadRequest();
  }, [id]);

  if (loading) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!request) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4">Investor Request Not Found</Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/marketplace')}
          sx={{ mt: 2 }}
        >
          Back to Marketplace
        </Button>
      </Container>
    );
  }

  const investorName = request.user
    ? `${request.user.firstName} ${request.user.lastName}`
    : request.investor;

  return (
    <Box sx={{ display: 'flex' }}>
      
      <MainContent isSidebarOpen={false}> 
        

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{ mb: 3 }}
          >
            <MuiLink 
              component="button" 
              variant="body2" 
              onClick={() => navigate('/')}
              color="inherit"
              sx={{ textDecoration: 'none' }}
            >
              Home
            </MuiLink>
            <MuiLink 
              component="button" 
              variant="body2" 
              onClick={() => navigate('/marketplace')}
              color="inherit"
              sx={{ textDecoration: 'none' }}
            >
              Marketplace
            </MuiLink>
            <Typography color="text.primary">Investor Request</Typography>
          </Breadcrumbs>

          {/* Back Button */}
          <Button
            variant="text"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/marketplace')}
            sx={{ mb: 3 }}
          >
            Back to Marketplace
          </Button>

          <MotionPaper
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            elevation={2}
            sx={{
              p: 4,
              borderRadius: 3,
              mb: 4,
              position: 'relative',
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.secondary.main, 0.2)}`
            }}
          >
            {/* Top decoration */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 6,
                background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main}, ${theme.palette.secondary.light})`
              }}
            />

            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
                  {request.projectName}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    {request.sector}
                  </Typography>
                </Box>

                {request.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <LocationIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      Preferred Location: {request.location}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <SectionTitle variant="h6">
                  Investment Request Goal
                </SectionTitle>
                <Typography variant="body1" paragraph>
                  {request.goal}
                </Typography>

                {request.preferredIndustry && (
                  <>
                    <SectionTitle variant="h6" sx={{ mt: 3 }}>
                      Preferred Industry Focus
                    </SectionTitle>
                    <Typography variant="body1" paragraph>
                      {request.preferredIndustry}
                    </Typography>
                  </>
                )}

                {request.proposal && (
                  <>
                    <SectionTitle variant="h6" sx={{ mt: 3 }}>
                      Proposal Requirements
                    </SectionTitle>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: alpha(theme.palette.secondary.light, 0.1),
                      borderRadius: 2,
                      borderLeft: `4px solid ${theme.palette.secondary.main}`
                    }}>
                      <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
                        {request.proposal}
                      </Typography>
                    </Box>
                  </>
                )}

                {request.investmentType && (
                  <>
                    <SectionTitle variant="h6" sx={{ mt: 3 }}>
                      Investment Type
                    </SectionTitle>
                    <Chip
                      label={request.investmentType}
                      color="secondary"
                      variant="outlined"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  </>
                )}

                {request.roi && (
                  <>
                    <SectionTitle variant="h6" sx={{ mt: 3 }}>
                      Investment Returns & Expectations
                    </SectionTitle>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: alpha(theme.palette.secondary.light, 0.1),
                      borderRadius: 2,
                      borderLeft: `4px solid ${theme.palette.secondary.main}`
                    }}>
                      <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
                        {request.roi}
                      </Typography>
                    </Box>
                  </>
                )}

                {request.links && request.links.length > 0 && (
                  <>
                    <SectionTitle variant="h6" sx={{ mt: 3 }}>
                      External Links
                    </SectionTitle>
                    <Box sx={{ mb: 3 }}>
                      {request.links.map((link, index) => (
                        <Button
                          key={index}
                          variant="text"
                          color="secondary"
                          endIcon={<LaunchIcon />}
                          sx={{ mb: 1, mr: 2 }}
                          onClick={() => window.open(link.url, '_blank')}
                        >
                          {link.title || link.url}
                        </Button>
                      ))}
                    </Box>
                  </>
                )}

                {request.documents && request.documents.length > 0 && (
                  <>
                    <SectionTitle variant="h6" sx={{ mt: 3 }}>
                      Available Documents
                    </SectionTitle>
                    <Box sx={{ mb: 3 }}>
                      {request.documents.map((doc, index) => (
                        <Chip
                          key={index}
                          icon={<DocumentIcon />}
                          label={doc.name}
                          variant="outlined"
                          sx={{ mr: 1, mb: 1 }}
                          onClick={() => window.open(doc.url, '_blank')}
                        />
                      ))}
                    </Box>
                  </>
                )}

                {request.nextSteps && (
                  <>
                    <SectionTitle variant="h6" sx={{ mt: 3 }}>
                      Next Steps
                    </SectionTitle>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: alpha(theme.palette.secondary.light, 0.1),
                      borderRadius: 2, 
                      mb: 3, 
                      borderLeft: `4px solid ${theme.palette.secondary.main}`
                    }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {request.nextSteps}
                      </Typography>
                    </Box>
                  </>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `2px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                    position: 'relative',
                    mb: 3,
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.secondary.main, 0.03)})`
                      : 'white',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: theme.shadows[8],
                      transform: 'translateY(-4px)',
                      borderColor: theme.palette.secondary.main
                    }
                  }}
                >
                  {/* Decorative gradient header */}
                  <Box
                    sx={{
                      height: 4,
                      background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main}, ${theme.palette.secondary.light})`
                    }}
                  />

                  {/* Company Header Section */}
                  <Box
                    sx={{
                      position: 'relative',
                      background: `linear-gradient(145deg, ${alpha(theme.palette.secondary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                      p: 3,
                      pb: 2
                    }}
                  >
                    {/* Decorative element */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 100,
                        height: 100,
                        background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)}, transparent)`,
                        borderRadius: '50%',
                        transform: 'translate(30%, -30%)'
                      }}
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                      <Avatar
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(request.organization)}&background=random&size=100&bold=true`}
                        alt={request.organization}
                        sx={{
                          width: 90,
                          height: 90,
                          border: `4px solid ${theme.palette.background.paper}`,
                          boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                          mb: 1.5,
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      />

                      <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center', mb: 0.3, fontSize: '1.05rem', lineHeight: 1.3 }}>
                        {request.investor}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.85rem', mb: 1.5 }}>
                        {request.organization}
                      </Typography>

                      {/* Inline Stats Chips */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Chip
                          icon={<Visibility sx={{ fontSize: '1rem' }} />}
                          label={`${request.viewCount || 0} views`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            bgcolor: alpha(theme.palette.secondary.main, 0.1),
                            color: 'secondary.main',
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                          }}
                        />
                        <Chip
                          icon={<BusinessIcon sx={{ fontSize: '1rem' }} />}
                          label={request.sector}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: 'info.main',
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Content Section */}
                  <Box sx={{ p: 2.5 }}>
                    {/* Quick Info Cards */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                      {request.location && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.secondary.main, 0.03),
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.08)}`,
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.secondary.main, 0.06),
                            borderColor: alpha(theme.palette.secondary.main, 0.15)
                          }
                        }}>
                          <LocationIcon sx={{ color: 'secondary.main', mr: 1.5, fontSize: '1.2rem' }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Location
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                              {request.location}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {request.investmentType && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            borderColor: alpha(theme.palette.primary.main, 0.15)
                          }
                        }}>
                          <MonetizationOn sx={{ color: 'primary.main', mr: 1.5, fontSize: '1.2rem' }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Investment Type
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                              {request.investmentType}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* CTA Buttons */}
                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      size="large"
                      onClick={() => setContactModalOpen(true)}
                      disabled={!currentUser || currentUser.id === request.userId}
                      sx={{
                        mb: 1.5,
                        borderRadius: 2.5,
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        textTransform: 'none',
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                        boxShadow: `0 4px 14px ${alpha(theme.palette.secondary.main, 0.4)}`,
                        '&:hover': {
                          boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.5)}`,
                          transform: 'translateY(-2px)',
                          transition: 'all 0.3s ease'
                        },
                        '&:active': {
                          transform: 'translateY(0)'
                        }
                      }}
                    >
                      📞 Contact Investor
                    </Button>

                    <Button
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      size="medium"
                      onClick={() => navigate('/marketplace')}
                      sx={{
                        borderRadius: 2.5,
                        py: 1.25,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          bgcolor: alpha(theme.palette.secondary.main, 0.08),
                          transform: 'translateY(-1px)',
                          transition: 'all 0.2s'
                        }
                      }}
                    >
                      ← Back to Marketplace
                    </Button>
                  </Box>
                </Paper>

                {similarRequests.length > 0 && (
                  <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Similar Investor Requests
                    </Typography>

                    {similarRequests.map(item => (
                      <Box key={item.id} sx={{ mb: 2 }}>
                        <MuiLink
                          component="button"
                          variant="subtitle1"
                          onClick={() => navigate(`/marketplace/request/${item.id}`)}
                          sx={{
                            fontWeight: 500,
                            display: 'block',
                            textAlign: 'left',
                            color: 'text.primary',
                            '&:hover': { color: theme.palette.secondary.main }
                          }}
                        >
                          {item.projectName}
                        </MuiLink>
                        <Typography variant="body2" color="text.secondary">
                          {item.organization}
                        </Typography>
                        <Divider sx={{ mt: 1 }} />
                      </Box>
                    ))}
                  </Paper>
                )}
              </Grid>
            </Grid>
          </MotionPaper>

          {/* Call to Action */}
          <MotionPaper
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ y: -8, scale: 1.01 }}
            elevation={2}
            sx={{
              p: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)}, ${alpha(theme.palette.secondary.main, 0.9)})`,
              position: 'relative',
              overflow: 'hidden',
              color: 'white',
              textAlign: 'center',
              border: `1px solid ${alpha(theme.palette.primary.light, 0.3)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.5)}`
              }
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Have a Project That Matches This Request?
              </Typography>

              <Typography variant="body1" sx={{ mb: 3, maxWidth: 800, mx: 'auto', opacity: 0.9 }}>
                If you have a project that aligns with this investor's requirements, let them know about it!
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={() => setContactModalOpen(true)}
                disabled={!currentUser || currentUser.id === request.userId}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.9)
                  },
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 4
                }}
              >
                Contact Investor
              </Button>
            </Box>
          </MotionPaper>
        </Container>

        <ContactModal
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          itemType="investor_request"
          itemId={request.id}
          itemName={request.projectName}
          ownerName={investorName}
          toUserId={request.userId}
        />
      </MainContent>
    </Box>
  );
};

export default InvestorRequestDetailPage;